<template>
    <div>
        <div class="d-flex justify-content-between">
            <h4 class="mb-1 table-title">P/L - {{ companyCurrency }}</h4>
            <div class="d-flex">
                <span class="text-danger mr-1 cursor-pointer" v-if="isFilterShown" @click="clearFilter"
                    >Clear Search</span
                >
                <font-awesome-icon
                    :icon="['far', 'filter']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="'Filter'"
                    @click="toggleFilter"
                />
                <font-awesome-icon
                    :icon="['far', 'compress-arrows-alt']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="`Filter out 0 values: ${isFilterZeroShown ? 'on' : 'off'}`"
                    @click="toggleFilterZero"
                />
                <font-awesome-icon
                    :icon="['far', 'cog']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="'Table Settings'"
                    v-b-modal.cp
                />
                <font-awesome-icon
                    :icon="['far', 'file-export']"
                    class="btn-icon font-medium-1"
                    @click="$bvModal.show('download-modal')"
                    v-b-tooltip.hover.top="'Export CSV'"
                />
            </div>
            <b-modal id="download-modal" title="Choose format type" @ok="download" centered>
                <v-select v-model="format" :clearable="false" label="format" :options="['.csv', '.xls']" />
            </b-modal>
        </div>
        <b-card no-body class="px-50 my-shadow">
            <CommonTable
                :isLoading="isLoading"
                :fields="fields"
                :items="dataAfterCollapsed"
                :max-display-rows="maxDisplayRowCount"
                :has-filter="true"
                :isFilterShown="isFilterShown"
                :isFilterZeroShown="isFilterZeroShown"
                @filterChange="filterChange"
                @filterClear="filterClear"
                :canFilterOutZeroValues="canFilterOutZeroValues"
                @filterOutZeroValues="handleFilterOutZeroValues"
                :field_sort="field_sort"
                :isDesc="isDesc"
                @onHeaderClick="(field) => sortBy(field)"
                ref="table"
            >
                <template #summary-row>
                    <b-tr class="text-right">
                        <b-td class="text-center bg-black" colspan="1">Total</b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Group')"
                            class="text-center bg-black"
                            colspan="1"
                        ></b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Agent')"
                            class="text-center bg-black"
                            colspan="1"
                        ></b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Current Equity')" class="bg-black">{{
                            formatMoney(dataTotal.CurrentEquity, 2)
                        }}</b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Current Credit')" class="bg-black">{{
                            formatMoney(dataTotal.CurrentCredit, 2)
                        }}</b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Withdrawable')"
                            class="bg-black"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.Withdrawable, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Daily Net Deposit')"
                            class="bg-black"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.NetDeposits, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Monthly Net Deposit')"
                            class="bg-black"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.MonthlyNetDeposit, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Daily Volume')"
                            class="bg-black"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.ClosedVolumeToday, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Monthly Volume')"
                            class="bg-black"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.MonthlyVolume, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Daily P/L')"
                            :class="dataTotal.ActualPL >= 0 ? 'bg-black text-success' : 'bg-black text-danger'"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.ActualPL, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Monthly P/L')"
                            :class="dataTotal.PLThisMonth >= 0 ? 'bg-black text-success' : 'bg-black text-danger'"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.PLThisMonth, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('Last Month P/L')"
                            :class="dataTotal.PLLastMonth >= 0 ? 'bg-black text-success' : 'bg-black text-danger'"
                            this.tableRowHeight
                            >{{ formatMoney(dataTotal.PLLastMonth, 2) }}</b-td
                        >
                    </b-tr>
                </template>
                <template #cell(name)="{ item }">
                    <div :style="{ 'padding-left': `${calcNamePadding(item.level)}px` }">
                        <dot v-if="isItemUncollapsible(item)" />
                        <span v-else class="cursor-pointer">
                            <font-awesome-icon
                                :icon="['fas', 'caret-down']"
                                size="lg"
                                class="mr-50"
                                @click="collapse(item)"
                                v-if="!collapsedSet.includes(item[whichpath])"
                            />
                            <font-awesome-icon
                                :icon="['fas', 'caret-right']"
                                size="lg"
                                class="mr-50"
                                @click="uncollapse(item)"
                                v-else
                            />
                        </span>
                        <NameField :name="item.name" />
                    </div>
                </template>
            </CommonTable>
            <div class="py-1 pr-1 d-flex justify-content-between card-footer-color">
                <FilterPills
                    :isLoading="isLoading"
                    v-model="activeLevels"
                    :levels="levels"
                    @toggleLevel="toggleLevel"
                />
                <div class="d-flex align-items-center">
                    <div class="mr-3 text-heading-color">
                        Accounts: {{ dataAfterCollapsed.filter((item) => isItemUncollapsible(item)).length }}
                    </div>
                    <RowCountPill v-model="maxDisplayRowCount" />
                </div>
            </div>
        </b-card>
        <TableSettingsModal
            :fields="companyPlFields.filter((i, index) => index > 0)"
            table="Company PL"
            @reactivity="key += 1"
            id="cp"
        />
    </div>
</template>
<script>
import CommonTable from "@/components/tables/CommonTable.vue";
import companyPlFields from "./CompanyPlFields.js";
import {
    formatMoney,
    exportFile,
    formatNumber,
    setFilterInLocalStorage,
    getFilterInLocalStorage,
    removeFilterInLocalStorage,
    isFilteredByRegion,
    getClientEmail,
    mergeTwoClients,
} from "@/helper";
import Ripple from "vue-ripple-directive";
import axios from "@axios";
import vSelect from "vue-select";
import FilterPills from "@/components/tables/LevelPills.vue";
import RowCountPill from "@/components/tables/RowCountPill.vue";
import Dot from "@/components/Dot";
import TableSettingsModal from "@/components/TableSettingModal";
import NameField from "@/components/tables/NameField.vue";
import moment from "moment";
import store from "@/store";

const TABLE_NAME = "PLTable";

const export_properties = ["login", "server", "group", "currency", "comment", "mt_account.agent_account"];
const export_values = [
    "CurrentEquityAccCurrency",
    "CurrentCreditAccCurrency",
    "WdableAccCurrency",
    "OverallLots",
    "ActualPLAccCurrency",
    "PLThisMonthAccCurrency",
    "PLOverallAccCurrency",
    "ClosedLotsToday",
    "#LotsThisMonth",
    "ActualPL",
    "PLThisMonth",
    "Withdrawable",
];

export default {
    components: {
        CommonTable,
        vSelect,
        FilterPills,
        Dot,
        TableSettingsModal,
        RowCountPill,
        NameField,
    },
    directives: {
        Ripple,
    },

    data() {
        return {
            key: 67,
            companyPL: [],
            isLoading: false,
            format: ".csv",
            companyPlFields,
            labels: [
                "CurrentEquity",
                "CurrentCredit",
                "Withdrawable",
                "NetDeposits",
                "MonthlyNetDeposit",
                "ClosedVolumeToday",
                "MonthlyVolume",
                "ActualPL",
                "PLThisMonth",
                "PLLastMonth",
            ],
            groupLevels: [],
            activeLevels: [],
            collapsedSet: [],
            maxDisplayRowCount: 19,
            isFilterShown: false,
            isFilterZeroShown: true,
            filterValues: {},
            canFilterOutZeroValues: true,
            filterOutZeroValues: {},
            ibs: [],

            field_sort: "CurrentEquity",
            isDesc: true,
            group_organization: {},
        };
    },

    computed: {
        companyCurrency() {
            const riskMangementSettings = store.getters["companySettings/settingsByPath"](["risk_management"]);
            if (riskMangementSettings.settings) return riskMangementSettings.settings.currency;
            return "";
        },

        export_header() {
            return [
                "Login",
                "Server",
                "Group",
                "Currency",
                "Comment",
                "Agent",
                "Current Equity",
                "Current Credit",
                "Current Wdable Amount",
                "Overall Lots",
                "Daily PL",
                `Monthly PL`,
                `Overall PL`,
                "Daily Volume (lots)",
                "Monthly Volume (lots)",
                `Daily P/L (${this.companyCurrency})`,
                `Monthly P/L (${this.companyCurrency})`,
                `Current Wdable Amount (${this.companyCurrency})`,
            ];
        },

        fields() {
            let restrict = JSON.parse(localStorage.getItem("Company PL Settings"));
            let results = companyPlFields;
            if (this.key && restrict) results = results.filter((i) => !restrict.includes(i.label));

            let agentField = results.find((item) => item.key == "agent");
            if (agentField) {
                agentField.filter.options = [
                    { value: "", text: "All" },
                    ...this.ibs.map((ib) => ({ value: ib.id, text: `${ib.id}. ${ib.user.full_name}` })),
                ];
            }
            return results;
        },

        fieldsMap() {
            return this.fields.reduce((res, val) => {
                return Object.assign(res, { [val.key]: val });
            }, {});
        },

        levels() {
            let result = this.groupLevels.map((levelName, index) => ({
                name: levelName,
                value: (item) => item.properties.path[index],
            }));
            return [
                ...result,
                {
                    name: "Client",
                    value: (item) =>
                        `${item.properties.mt_account?.name ?? ""} ${item.properties.mt_account?.email ?? ""}`,
                },
                { name: "Account", value: (item) => `${item.properties.server}-${item.properties.login}` },
            ];
        },

        whichpath() {
            let res = "path";
            if (!isFilteredByRegion(this.activeLevels, this.groupLevels.length)) {
                res = "subpath";
            }
            return res;
        },

        dataTotal() {
            let result = this.calculateSum(
                this.labels,
                this.dataAfterGroup.filter((item) => item.level == this.groupLevels.length + 2)
            );
            return result;
        },

        dataAfterCollapsed() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFormat));
            this.collapsedSet.forEach((path) => {
                results = results.filter((item) => !this.isChildOfParent(item[this.whichpath], path));
            });
            // console.log(results)
            return results;
        },

        dataAfterFormat() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFilteredByZero));
            results = results.map((item) => {
                this.labels.forEach((field) => {
                    item[field] = formatMoney(item[field], 2);
                });
                item.agent = item.agent || "";
                return item;
            });
            return results;
        },

        dataAfterFilteredByZero() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFiltered));

            Object.keys(this.filterOutZeroValues).forEach((key) => {
                if (this.filterOutZeroValues[key]) {
                    results = results.filter((item) => {
                        if (key != "Risk") return !isNaN(parseFloat(item[key])) && parseFloat(item[key]) !== 0;
                        else return item[key] !== 1;
                    });
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },

        dataAfterFiltered() {
            let results = JSON.parse(JSON.stringify(this.dataAfterSort));
            Object.keys(this.filterValues).forEach((key) => {
                let field = this.fields.find((field) => field.key == key);
                if (field?.filter?.filterFunc) {
                    // console.log(results, this.filterValues[key])
                    results = field.filter.filterFunc(results, this.filterValues[key]);
                } else {
                    if (this.filterValues[key]) {
                        results = results.filter((item) => {
                            return (item[key] ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(this.filterValues[key].toString().toLowerCase());
                        });
                    }
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },

        dataAfterSort() {
            let desc_or_asc = this.isDesc ? -1 : 1;
            let result = [];
            let j = 0;
            this.activeLevels.forEach((level, index) => {
                let pos_or_neg = index == 0 ? desc_or_asc : -desc_or_asc;
                let data = this.dataAfterGroup.filter((datum) => datum.level == level);
                data.sort((a, b) => {
                    let x = a[this.field_sort];
                    let y = b[this.field_sort];
                    if (this.fieldsMap[this.field_sort].displayAbsolute) {
                        x = Math.abs(x);
                        y = Math.abs(y);
                    }
                    return pos_or_neg * (x < y ? -1 : x > y ? 1 : 0);
                });

                // Check if table is filtered by region, if not we have to merge client with same email (diffrent region)
                if (
                    !isFilteredByRegion(this.activeLevels, this.groupLevels.length) &&
                    level === this.groupLevels.length + 1
                ) {
                    let noDuplicatedData = {};
                    data.forEach((datum) => {
                        if (noDuplicatedData[datum.name]) {
                            noDuplicatedData[datum.name] = mergeTwoClients(
                                noDuplicatedData[datum.name],
                                datum,
                                this.fields
                            );
                        } else noDuplicatedData[datum.name] = datum;
                    });
                    data = Object.values(noDuplicatedData);
                }

                if (index == 0) {
                    result = result.concat(data);
                    j = level;
                } else {
                    // Find correct index and insert the value
                    // console.log(level, index, j, data)
                    data.forEach((current) => {
                        result.forEach((datum, index) => {
                            if (
                                datum.level == j &&
                                this.isChildOfParent(current[this.whichpath], datum[this.whichpath])
                            ) {
                                result.splice(index + 1, 0, current);
                            }
                        });
                    });
                    j = level;
                }
            });
            // console.log("dataAfterSort", results)
            return result;
        },

        dataAfterGroup() {
            let results = [];
            this.levels.forEach((level, index) => {
                if (this.activeLevels.includes(index + 1)) {
                    let distinctValues = [
                        ...new Set(
                            this.items.map((item) => {
                                let path = item.path.split("/").slice(0, index + 1);
                                if (this.whichpath == "subpath") {
                                    path = path.slice(Math.min(...this.activeLevels) - 1);
                                }
                                return path.join("/");
                            })
                        ),
                    ];

                    results.push(
                        ...distinctValues.map((path) => {
                            let name = path.split("/");
                            name = this.group_organization[path]?.name ?? name[name.length - 1];

                            let data = this.items.filter(
                                (item) =>
                                    this.isChildOfParent(item[this.whichpath], path) || item[this.whichpath] == path
                            );
                            let tmp = this.calculateSum(this.labels, data);

                            if (index == this.levels.length - 1) {
                                tmp = {
                                    ...data[0],
                                    ...tmp,
                                };
                            }

                            return {
                                ...tmp,
                                name: name,
                                level: index + 1,
                                isUncollapsible: index + 1 === Math.max(...this.activeLevels),
                                [this.whichpath]: path,
                            };
                        })
                    );
                }
            });
            return results;
        },

        items() {
            return this.companyPL
                .filter((item) => item?.properties?.path)
                .map((item) => {
                    let groupPath = item.properties.path.join("/");
                    let group = item.properties.group.split("\\");
                    Object.keys(this.group_organization).forEach((key) => {
                        if (key === groupPath) group = this.group_organization[key].name;
                    });
                    let tmp = this.labels.reduce((obj, key) => {
                        obj[key] = item.value?.[key] ?? NaN;
                        return obj;
                    }, {});

                    let path = this.levels.map((level) => level.value(item));
                    let subpath = path.slice(Math.min(...this.activeLevels) - 1);
                    return {
                        ...tmp,
                        level: this.groupLevels.length + 2,
                        path: path.join("/"),
                        subpath: subpath.join("/"),
                        name: item.properties.server + "-" + item.properties.login,
                        agent: item.properties.mt_account?.agent_account,
                        group,
                    };
                });
        },
    },

    methods: {
        sortBy(field) {
            if (field) {
                this.field_sort = field;
                this.isDesc = !this.isDesc;
            }
        },
        isChildOfParent(child, parent) {
            let child_path = child.split("/");
            let parent_path = parent.split("/");
            return (
                child_path.length > parent_path.length && child_path.slice(0, parent_path.length).join("/") == parent
            );
        },
        download() {
            if (this.format == ".csv") this.exportCSV();
            else this.exportXlsx();
        },
        exportCSV() {
            axios
                .get(`${process.env.VUE_APP_API_URL}metric/CurrentUserStatistics/export_csv`, {
                    params: {
                        header: JSON.stringify(this.export_header),
                        properties: JSON.stringify(export_properties),
                        values: JSON.stringify(export_values),
                    },
                })
                .then((res) => {
                    const anchor = document.createElement("a");
                    anchor.href = "data:text/csv;charset=utf-8," + encodeURIComponent(res.data);
                    anchor.target = "_blank";
                    anchor.download = `CompanyPL ${moment().format("YYYY-MM-DD HH:mm:ss")}.csv`;
                    anchor.click();
                });
        },
        exportXlsx() {
            axios({
                method: "get",
                url: `${process.env.VUE_APP_API_URL}metric/CurrentUserStatistics/export_xlsx`,
                params: {
                    header: JSON.stringify(this.export_header),
                    properties: JSON.stringify(export_properties),
                    values: JSON.stringify(export_values),
                },
                responseType: "blob",
            }).then((res) => {
                var fileURL = window.URL.createObjectURL(new Blob([res.data]));
                var fileLink = document.createElement("a");

                fileLink.href = fileURL;
                fileLink.setAttribute("download", `CompanyPL ${moment().format("YYYY-MM-DD HH:mm:ss")}.xls`);
                document.body.appendChild(fileLink);

                fileLink.click();
            });
        },

        formatMoney,

        initFilter() {
            if (getFilterInLocalStorage(TABLE_NAME)) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME);

                this.isFilterShown = true;
                this.$refs.table.initFilter(filterValues);
            }
            if (getFilterInLocalStorage(TABLE_NAME + "Zero")) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME + "Zero");
                this.isFilterZeroShown = filterValues.show ? true : false;
                this.$refs.table.initFilterZero(filterValues);
            }
        },

        toggleFilterZero() {
            this.isFilterZeroShown = !this.isFilterZeroShown;
            if (this.isFilterZeroShown) {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: true,
                });
            } else {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: false,
                });
            }
        },

        toggleFilter() {
            this.isFilterShown = !this.isFilterShown;
            if (this.isFilterShown) {
                setFilterInLocalStorage(TABLE_NAME, {});
            } else {
                removeFilterInLocalStorage(TABLE_NAME);
            }
        },

        clearFilter() {
            this.$refs.table.clearFilter();
        },

        filterChange(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        filterClear(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        handleFilterOutZeroValues(filterOutZeroValues) {
            Object.keys(filterOutZeroValues).forEach((key) => {
                if (key != "show") this.$set(this.filterOutZeroValues, key, filterOutZeroValues[key]);
            });
            if (this.isFilterZeroShown)
                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: this.isFilterZeroShown,
                });
        },

        update_metric_data(datum) {
            if (
                datum.metric_definition_name &&
                ["CurrentUserStatistics"].includes(datum.metric_definition_name) &&
                !this.isLoading
            ) {
                var flag = false;
                this.companyPL = this.companyPL.map((item) => {
                    if (item.id == datum.id) {
                        flag = true;
                        return { ...item, value: datum.value };
                    }
                    return item;
                });
                if (!flag) this.companyPL.push(datum);
            }
        },

        calculateSum(labels, data) {
            return labels.reduce((obj, key) => {
                if (data.length == 1) {
                    obj[key] = data[0][key];
                } else {
                    obj[key] = data.reduce((sum, item) => sum + (isNaN(item[key]) ? 0 : item[key]), 0);
                }
                return obj;
            }, {});
        },

        isItemUncollapsible(item) {
            return item.level == Math.max(...this.activeLevels);
        },

        collapse(item) {
            this.collapsedSet.push(item[this.whichpath]);
        },

        uncollapse(item) {
            this.collapsedSet = this.collapsedSet.filter((path) => path != item[this.whichpath]);
        },

        calcNamePadding(level) {
            return 25 * this.activeLevels.indexOf(level);
        },

        toggleLevel(level) {
            // console.log(level, this.activeLevels)
            this.collapsedSet = this.collapsedSet.filter((path) => path.split("/").length >= level);
            if (this.$refs.table) {
                this.$refs.table.handleHeaderHeight(true);
            }
        },

        getAllChildren(node, path) {
            let result = { [path]: node.settings };
            if (node.children) {
                Object.keys(node.children).forEach((key) => {
                    result = { ...result, ...this.getAllChildren(node.children[key], path + "/" + key) };
                });
            }
            return result;
        },
    },

    channels: {
        MetricDataUpdateChannel: {
            connected() {
                console.log("Connected to the MetricDataUpdateChannel channel");
            },
            rejected() {
                console.log("Rejected to the MetricDataUpdateChannel channel");
            },
            received(data) {
                if (data?.event_name == "trade.MetricDatum.before_destroy") {
                    this.companyPL = this.companyPL.filter((datum) => {
                        return datum.id != data.payload.id;
                    });
                    return;
                }

                if (data?.event_name == "trade.MetricDatum.after_create") {
                    this.update_metric_data(data.payload);
                    return;
                }

                data.forEach((datum) => {
                    // console.log(datum)
                    this.update_metric_data(datum);
                });
                this.$store.commit("app/SET_DATA_LIVE_TIME", moment().format(" HH:mm:ss - DD/MM/YYYY"));
            },
            disconnected() {
                console.log("Disconnected to the MetricDataUpdateChannel channel");
            },
        },
    },

    mounted() {
        this.initFilter();

        this.isLoading = true;
        axios
            // .get(`${process.env.VUE_APP_API_URL}metric/CurrentUserStatistics/data`)
            //? mockData
            .get("CurrentUserStatistics/data")
            .then((res) => {
                this.isLoading = false;
                this.companyPL = res.data;
            })
            .catch((err) => {
                console.error(err);
                this.isLoading = false;
            });

        //? mockData
        // this.$cable.subscribe({
        //     channel: "MetricDataUpdateChannel",
        // });

        axios
            // .get(`${process.env.VUE_APP_API_URL}site_settings/risk_management/group_levels`)
            // mockData
            .get("/GroupLevel/data")
            .then((res) => {
                this.groupLevels = res.data.settings;
                this.activeLevels = [this.groupLevels.length + 2];
            });

        axios
            // .get(`${process.env.VUE_APP_API_URL}partners`)
            //? mockData
            .get("Partners/data")
            .then((res) => {
                this.ibs = res.data;
            });

        axios
            // .get(`${process.env.VUE_APP_API_URL}site_settings/risk_management/group_organization?recursive`)
            //? mockData
            .get("/GroupOrganization/data")
            .then((res) => {
                this.group_organization = {};
                if (res.data?.children) {
                    Object.keys(res.data.children).forEach((key) => {
                        this.group_organization = {
                            ...this.group_organization,
                            ...this.getAllChildren(res.data.children[key], key),
                        };
                    });
                }
            })
            .catch((err) => console.error(err));
    },
};
</script>
